import { useAppDispatch, useAppSelector } from 'app/config/store';
import React, { useEffect, useState } from 'react';

import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Spin, Table, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import Title from 'antd/es/typography/Title';
import CommentComponent from 'app/entities/comment/comment';
import ModalConfirmComponent from 'app/shared/component/modal/confirm';
import ModalRejectComponent from 'app/shared/component/modal/reject';
import { formatPoint, percent } from 'app/shared/util/percent';
import dayjs from 'dayjs';
import * as _ from 'lodash';
import { Container } from 'reactstrap';
import { columnsResult, minusPoints, plusPoints } from '../config';
import { confirmComplete, getEvaluateConfirmComplete, getOtherComment, getOtherMinusComment, getOtherPlusComment, getOtherPoints, rejectProcess, reset, saveDraft } from './confirm-complete.reducer';
import OtherCommentEvaluationComponent from './other-comment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OtherGeneralCommentComponent from '../other-comment';
import { getCurrentPlanPeriodOpen, getRankByPoint, getRanks } from 'app/entities/common/common.reducer';
import OtherPointsEvaluationComponent from './other-point';
import { exportExcel } from 'app/entities/evaluation-level/evaluation-level.reducer';
import { saveAs } from 'file-saver';

type IConfirmCompleteEvaluationProps = {
    isOpen: boolean;
    onClose: (isReload?: boolean) => void;
    accountTableName: string;
    accountTableId: string;
    isReadOnly?: boolean;
    planPeriodId?: string;
};

export const EvaConfirmCompleteEvaluation = ({ isOpen, onClose, accountTableName, accountTableId, isReadOnly, planPeriodId }: IConfirmCompleteEvaluationProps) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const updating = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.updating);
    const loading = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.loading);
    const entities = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.entities);
    const otherComment = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.otherComment);
    const otherPoint = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.otherPoint);
    const updateSuccess = useAppSelector(state => state.evaluateConfirmCompleteEvaluation.updateSuccess);
    const account = useAppSelector(state => state.authentication.account);
    const currentPlanPeriod = useAppSelector(state => state.common.currentPlanPeriod);
    const ranks = useAppSelector(state => state.common.ranks);

    // Init state
    const [rankName, setRankName] = useState(null);
    const [isOpenDrawerComment, setOpenDrawerComment] = useState(false);
    const [isOpenModalConfirmComplete, setOpenModalConfirmComplete] = useState(false);
    const [isOpenModalRejectProcess, setOpenModalRejectProcess] = useState(false);
    const [isOpenModalOtherComment, setOpenModalOtherComment] = useState(false);
    const [isOpenModalOtherGeneralComment, setOpenModalOtherGeneralComment] = useState(false);
    const [currentIndexCriteria, setCurrentIndexCriteria] = useState(null);
    const [isLoadingComment, setLoadingComment] = useState(false);
    const [isOpenModalOtherPoint, setOpenModalOtherPoint] = useState(false);
    const [isLoadingPoint, setLoadingPoint] = useState(false);

    // watch data for changes
    const totalPlusPoints = Form.useWatch('totalPlusPoint', form)
    const totalMinusPoints = Form.useWatch('totalMinusPoint', form)

    useEffect(() => {
        dispatch(getCurrentPlanPeriodOpen());

    }, []);

    useEffect(() => {
        innitData()
    }, [accountTableId, currentPlanPeriod]);

    useEffect(() => {
        form.setFieldsValue({
            generalFeedback: entities?.evaluateOfLevel?.generalFeedback,
            plusPoint: mapPoints(entities?.selfEvaluation?.plusPoint, entities?.evaluateOfLevel?.plusPoint),
            minusPoint: mapPoints(entities?.selfEvaluation?.minusPoint, entities?.evaluateOfLevel?.minusPoint),
            totalPlusPoint: calculateTotalWithFormat(entities?.evaluateOfLevel?.plusPoint),
            totalMinusPoint: calculateTotalWithFormat(entities?.evaluateOfLevel?.minusPoint),
            userRankingEvaluateId: entities?.evaluateOfLevel?.userRankingEvaluateId,
        });
    }, [entities]);

    useEffect(() => {
        if (updateSuccess) {
            handleClose(updateSuccess);
        }
    }, [updateSuccess]);

    useEffect(() => {
        if (entities) {
            const totalResultPoints = entities?.totalEvaluateScore + percent(totalPlusPoints) - percent(totalMinusPoints)
            if (totalResultPoints && !_.isNaN(percent(totalResultPoints))) {
                handleGetRankByPoint(totalResultPoints);
            }
            form.setFieldsValue({ totalResultPoint: formatPoint(totalResultPoints) })
        }
    }, [totalPlusPoints, totalMinusPoints, entities]);

    const mapPoints = (points, comments = []) =>
        points?.map((x, idx) => ({
            ...x,
            point: x.point && formatPoint(x.point),
            evaluateExplain: comments && comments[idx]?.explain,
            evaluatePoint: comments && comments[idx].point && formatPoint(comments[idx]?.point)
        }));

    const calculateTotalWithFormat = (points) =>
        formatPoint(_.sumBy(points, (o: any) => o.point));

    const innitData = () => {
        if (accountTableId && Object.keys(currentPlanPeriod).length > 0) {
            dispatch(getRanks({ partyId: accountTableId, year: currentPlanPeriod?.label }))
            dispatch(getEvaluateConfirmComplete({ ownerId: accountTableId, userId: account?.id, planPeriodId: planPeriodId || currentPlanPeriod?.value }));
        }
    };

    const mapperDataPayload = (values) => {
        const payload = {
            ...values,
            ownerId: accountTableId,
            userId: account?.id,
            evaluateLevelId: entities?.evaluateLevelId,
            planPeriodId: planPeriodId || currentPlanPeriod?.value,
            checkinPoint: entities?.krYearStatistic?.checkinPoint,
            year: currentPlanPeriod?.label,
            documentId: entities?.documentId,
            plusPoint: values?.plusPoint?.map(x => (
                { explain: x.evaluateExplain, point: x.evaluatePoint ? percent(x.evaluatePoint) : null }
            )),
            minusPoint: values?.minusPoint?.map(x => (
                { explain: x.evaluateExplain, point: x.evaluatePoint ? percent(x.evaluatePoint) : null }
            )),
        };
        return payload;
    }

    const handleSubmit = () => {
        handleOpenModalConfirmComplete()
    };

    const handleReject = () => {
        handleOpenModalRejectProcess()
    }

    const handleSaveDraft = () => {
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(saveDraft(payload));
        }).catch(() => { return; });
    };

    const handleClose = (reload = false) => {
        onClose(reload);
        dispatch(reset())
    };

    const handleShowDrawerComment = () => {
        setOpenDrawerComment(true);
    };

    const handleCloseDrawerComment = () => {
        setOpenDrawerComment(false);
    };

    const handleOpenModalConfirmComplete = () => {
        setOpenModalConfirmComplete(true);
    }

    const handleConfirmComplete = () => {
        setOpenModalConfirmComplete(false);
        form.validateFields().then(values => {
            const payload = mapperDataPayload(values);
            dispatch(confirmComplete(payload));

        }).catch(() => { return; });
    }

    const handleOpenModalRejectProcess = () => {
        setOpenModalRejectProcess(true);
    }

    const handleCancelProcess = (reason: string) => {
        setOpenModalRejectProcess(false)
        const values = form.getFieldsValue(); // Get field values directly
        const payload = {
            ...mapperDataPayload(values),
            reason
        };
        dispatch(rejectProcess(payload));
        handleClose(true);
    }

    const handleOpenModalOtherComment = () => {
        setOpenModalOtherComment(true);
    }

    const handleCloseModalOtherComment = () => {
        setOpenModalOtherComment(false);
    }

    const handleGetOtherPlusComments = (index: number) => {
        setCurrentIndexCriteria(index);
        handleOpenModalOtherComment()
        dispatch(getOtherPlusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: planPeriodId || currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleGetOtherMinusComments = (index: number) => {
        setCurrentIndexCriteria(index);
        handleOpenModalOtherComment()
        dispatch(getOtherMinusComment({ index, ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: planPeriodId || currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleOpenModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(true);
    }

    const handleCloseModalOtherGeneralComment = () => {
        setOpenModalOtherGeneralComment(false);
    }

    const handleGetOtherComments = () => {
        handleOpenModalOtherGeneralComment()
        dispatch(getOtherComment({ ownerId: accountTableId, documentId: entities?.documentId, evaluateLevelId: entities?.evaluateLevelId, planPeriodId: planPeriodId || currentPlanPeriod?.value }))
            .finally(() => { setLoadingComment(false) });
    }

    const handleGetRankByPoint = (point) => {
        dispatch(getRankByPoint({ partyId: accountTableId, year: currentPlanPeriod?.label, point })).then((res) => {

            if (res && res.payload) {
                const result = (res.payload as any).data

                setRankName(`${result?.code} - ${result?.name}`)
            }
        });
    }

    // Handle changes in form values to update the total points
    const handleValuesChange = (__, allValues) => {
        const calculateTotal = (points) =>
            points?.reduce((sum, item) => sum + (item.evaluatePoint || 0), 0) || 0;

        form.setFieldsValue({
            totalPlusPoint: calculateTotal(allValues.plusPoint),
            totalMinusPoint: calculateTotal(allValues.minusPoint),
        });
    };

    const handleOpenModalOtherPoint = () => {
        setOpenModalOtherPoint(true);
    }

    const handleCloseModalOtherPoint = () => {
        setOpenModalOtherPoint(false);
    }

    const handleGetOtherPoints = () => {
        setLoadingPoint(true)
        handleOpenModalOtherPoint()
        dispatch(getOtherPoints({ ownerId: accountTableId, reportScheduleId: currentPlanPeriod?.value, planPeriodId: planPeriodId || currentPlanPeriod?.value }))
            .finally(() => { setLoadingPoint(false) });
    }

    const handleExport = () => {
        dispatch(
            exportExcel({ ownerId: accountTableId, reportScheduleId: currentPlanPeriod?.value })).then(res => {
                if (res.payload) {
                    const resultExcel = res.payload as any;
                    saveAs(
                        new Blob([resultExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
                        `Kết quả_${dayjs().format('DDMMYYYYhhmmss')}`,
                    );
                }
            });
    }

    return (
        <Modal
            width={'90vw'}
            open={isOpen}
            footer={null}
            onCancel={() => {
                handleClose();
            }}
            maskClosable={false}
        >
            <Spin spinning={loading || updating} delay={300}>
                <Form initialValues={{
                    plusPoint: plusPoints,
                    minusPoint: minusPoints
                }} size="large" form={form} onValuesChange={handleValuesChange} onFinish={handleSubmit} disabled={isReadOnly} layout="vertical">
                    <Row>
                        <Col md={20}>
                            <Title level={3}>{`Xác nhận hoàn thành đánh giá ${accountTableName ? `- ${accountTableName}` : ''}`}</Title>
                        </Col>
                        <Col md={1} />
                        <Col md={3}><Button disabled={updating} onClick={handleShowDrawerComment}>Ý kiến trao đổi</Button></Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <Form.Item label="Kỳ báo cáo">
                                <Input disabled value={currentPlanPeriod?.label} />
                            </Form.Item>
                        </Col>
                        <Col md={18} />
                        {/* <Col md={3}><Button className='green' onClick={handleExport}>Xuất Excel</Button></Col> */}
                    </Row>
                    <Row className='my-3'>
                        <Col md={11} />
                        <Col md={3}>
                            <span className='fw-bold'>Đơn vị tự đánh giá</span>
                        </Col>
                        <Col md={1} />
                        <Col>
                            <span className='fw-bold'>{`${entities?.evaluateLevelName} - ${entities?.performerName}`}</span>
                        </Col>
                    </Row>
                    <Row className='my-3'>
                        <Col md={11}>
                            <span className="fw-bold h5">I. ĐIỂM ĐÁNH GIÁ THỰC HIỆN MỤC TIÊU</span>
                            <span className='ps-2'>
                                <Tooltip title="Xem ý kiến khác">
                                    <FontAwesomeIcon
                                        onClick={() => {
                                            handleGetOtherPoints();
                                        }}
                                        icon="eye"
                                        color='blue'
                                    />
                                </Tooltip>
                            </span>
                        </Col>
                        <Col md={3}>
                            <Form.Item>
                                <InputNumber value={formatPoint(entities?.krYearStatistic?.checkinPoint)}
                                    disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col md={1} />
                        <Col md={3}>
                            <Form.Item>
                                <InputNumber value={formatPoint(entities?.totalEvaluateScore)}
                                    disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className='my-3 ps-3'>
                        <Col md={24}>
                            <span className="fw-bold h6">a. Xem thống kế số lượng theo tình trạng thực hiện</span>
                        </Col>
                    </Row>
                    <Row className='ps-3'>
                        <Col md={24}>
                            <Table
                                pagination={false}
                                bordered
                                dataSource={entities?.krYearStatistic?.krStatistics.map(x => ({ ...x, key: x.name }))}
                                columns={columnsResult}
                                className="w-100"
                                rowClassName={(record, index) => {
                                    return index === entities?.krYearStatistic?.krStatistics.length - 1 ? 'fw-bold' : '';
                                }}
                            />
                        </Col>
                    </Row>

                    <Row className='my-3 ps-2'>
                        <Col>
                            <span className="fw-bold h6">ĐIỂM CỘNG</span>
                        </Col>
                    </Row>
                    <Row className='my-3'>
                        <Col md={7} />
                        <span>Đơn vị tự đánh giá</span>
                        <Col md={7} />
                        <span>{`${entities?.evaluateLevelName} - ${entities?.performerName}`}</span>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={7} />
                        <Col md={2}>
                            <Form.Item>
                                <InputNumber value={calculateTotalWithFormat(entities?.selfEvaluation?.plusPoint)} disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col md={7} />
                        <Col md={2}>
                            <Form.Item
                                name={'totalPlusPoint'}
                                rules={[{ max: 5, type: 'integer', message: 'Tổng điểm cộng không được lớn hơn 5%' }]}
                            >
                                <InputNumber disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.List name="plusPoint">
                        {(fields) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row key={key} className='my-3 ps-3'>
                                        <Col md={6}>
                                            <span className="fw-bold">{`${name + 1}. ${plusPoints[name].name}`}</span>
                                            <span className='ps-3'>
                                                <Tooltip title="Xem ý kiến khác">
                                                    <FontAwesomeIcon
                                                        onClick={() => {
                                                            handleGetOtherPlusComments(name);
                                                        }}
                                                        icon="eye"
                                                        color='blue'
                                                        className='cursor-pointer'
                                                    />
                                                </Tooltip>
                                            </span>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={2}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'point']}
                                            >
                                                <InputNumber disabled className='w-100' min={1} addonAfter="%" />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'explain']}>
                                                <TextArea disabled className='w-100' rows={1} />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={2}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'evaluatePoint']}
                                            >
                                                <InputNumber className='w-100' onChange={() => { form.validateFields([['plusPoint', name, 'evaluateExplain'], 'totalPlusPoint']) }} min={1} addonAfter="%" />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'evaluateExplain']}
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator(__, value) {
                                                            const pointValue = getFieldValue(['plusPoint', name, 'evaluatePoint']);
                                                            if (pointValue > 0 && !value) {
                                                                return Promise.reject(new Error('VUi lòng nhập'));
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    }),
                                                ]}>
                                                <TextArea placeholder='Điền ý kiến' className='w-100' rows={1} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ))}
                            </>
                        )}
                    </Form.List>
                    <Row className='my-3 ps-2'>
                        <Col md={8}>
                            <span className="fw-bold h6">ĐIỂM TRỪ</span>
                        </Col>
                    </Row>
                    <Row className='my-3'>
                        <Col md={7} />
                        <span> Đơn vị tự đánh giá</span>
                        <Col md={7} />
                        <span> {`${entities?.evaluateLevelName} - ${entities?.performerName}`}</span>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={7} />
                        <Col md={2}>
                            <Form.Item>
                                <InputNumber value={calculateTotalWithFormat(entities?.selfEvaluation?.minusPoint)} disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col md={7} />
                        <Col md={2}>
                            <Form.Item
                                name={'totalMinusPoint'}
                            >
                                <InputNumber disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.List name="minusPoint">
                        {(fields) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row key={key} className='my-3 ps-3'>
                                        <Col md={6}>
                                            <span className="fw-bold">{`${name + 1}. ${minusPoints[name].name}`}</span>
                                            <span className='ps-3'>
                                                <Tooltip title="Xem ý kiến khác">
                                                    <FontAwesomeIcon
                                                        onClick={() => {
                                                            handleGetOtherMinusComments(name);
                                                        }}
                                                        icon="eye"
                                                        color='blue'
                                                        className='cursor-pointer'
                                                    />
                                                </Tooltip>
                                            </span>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={2}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'point']}>
                                                <InputNumber disabled className='w-100' min={1} addonAfter="%" />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'explain']}>
                                                <TextArea className='w-100' disabled rows={1} />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={2}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'evaluatePoint']}
                                            >
                                                <InputNumber className='w-100' onChange={() => { form.validateFields([['minusPoint', name, 'evaluateExplain'], 'totalMinusPoint']) }} min={1} addonAfter="%" />
                                            </Form.Item>
                                        </Col>
                                        <Col md={1} />
                                        <Col md={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'evaluateExplain']}
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator(__, value) {
                                                            const pointValue = getFieldValue(['minusPoint', name, 'evaluatePoint']);
                                                            if (pointValue > 0 && !value) {
                                                                return Promise.reject(new Error('VUi lòng nhập'));
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    }),
                                                ]}>
                                                <TextArea placeholder='Điền ý kiến' className='w-100' rows={1} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ))}
                            </>
                        )}
                    </Form.List>
                    <hr />
                    <Row className='my-3'>
                        <Col md={24}>
                            <span className="fw-bold h5">II. TỔNG HỢP KẾT QUẢ ĐÁNH GIÁ</span>
                        </Col>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={8} />
                        <Col md={1} />
                        <Col md={3}>
                            <span className="fw-bold">Đơn vị tự đánh giá</span>
                        </Col>
                        <Col md={4} />
                        <Col>
                            <span className="fw-bold"> {`${entities?.evaluateLevelName} - ${entities?.performerName}`}</span>
                        </Col>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={8}>
                            <span className="fw-bold h6">1. TỔNG ĐIỂM ĐÁNH GIÁ</span>
                        </Col>
                        <Col md={1} />
                        <Col md={3}>
                            <Form.Item
                            >
                                <InputNumber value={formatPoint(entities?.selfEvaluation?.pointTotal)} disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                        <Col md={4} />
                        <Col md={3}>
                            <Form.Item
                                name={'totalResultPoint'}
                            >
                                <InputNumber disabled className='w-100' addonAfter="%" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={8}>
                            <span className="fw-bold h6">2. KẾT QUẢ XẾP LOẠI THEO TỔNG ĐIỂM</span>
                        </Col>
                        <Col md={1} />
                        <Col md={6}>
                            <Form.Item>
                                <Input disabled value={`${entities?.selfEvaluation?.rankCode} - ${entities?.selfEvaluation?.rankName}`} className='w-100' />
                            </Form.Item>
                        </Col>
                        <Col md={1} />
                        <Col md={6}>
                            <Form.Item
                            >
                                <Input value={rankName} disabled className='w-100' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className='my-3 ps-2'>
                        <Col md={8}>
                            <span className="fw-bold h6">3. KẾT QUẢ XẾP LOẠI CUỐI CÙNG<span className='text-danger mb-2'>*</span></span>
                        </Col>
                        <Col md={1} />
                        <Col md={6}>
                            <Form.Item
                            >
                                <Input disabled value={entities?.selfEvaluation?.userRankingEvaluateName} className='w-100' />
                            </Form.Item>
                        </Col>
                        <Col md={1} />
                        <Col md={6}>
                            <Form.Item
                                name={'userRankingEvaluateId'}
                                rules={[{ required: true, message: 'Vui lòng nhập' }]}
                            >
                                <Select options={ranks?.map(x => ({
                                    label: x.name,
                                    value: x.id,
                                }))} className='w-100' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <hr />
                    <Row className='my-3'>
                        <Col>
                            <span className="fw-bold h5">III. Ý KIẾN CHUNG</span>
                            <span className='ps-2'>
                                <Tooltip title="Xem ý kiến khác">
                                    <FontAwesomeIcon
                                        onClick={() => {
                                            handleGetOtherComments();
                                        }}
                                        icon="eye"
                                        color='blue'
                                        className='cursor-pointer'
                                    />
                                </Tooltip>
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={24}>
                            <Form.Item label="Đơn vị được đánh giá">
                                <TextArea disabled className='w-100' value={entities?.selfEvaluation?.generalFeedback} rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={24}>
                            <Form.Item label={`${entities?.evaluateLevelName} - ${entities?.performerName}`} name={'generalFeedback'}
                                rules={[{ required: true, message: 'Vui lòng nhập' }]}>
                                <TextArea className='w-100' rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item>
                        <Row className="action mt-3 d-flex gap-2" justify="end">
                            <Col>
                                <Button
                                    onClick={() => {
                                        handleClose();
                                    }}
                                    disabled={updating}
                                >
                                    Hủy
                                </Button>
                            </Col>
                            <Col>
                                <Button
                                    className="green"
                                    onClick={() => {
                                        handleSaveDraft();
                                    }}
                                    disabled={updating || isReadOnly}
                                >
                                    Lưu nháp
                                </Button>
                            </Col>
                            <Col>
                                {entities?.isFinalEvaluateLevel ?
                                    <Button type="primary" htmlType="submit" disabled={updating || isReadOnly}>
                                        Hoàn thành đánh giá
                                    </Button> :
                                    <Button type="primary" htmlType="submit" disabled={updating || isReadOnly}>
                                        Hoàn thành đánh giá và gửi cấp tiếp theo
                                    </Button>
                                }
                            </Col>
                            <Col>
                                <Button className='warning'
                                    onClick={() => {
                                        handleReject();
                                    }} disabled={updating || isReadOnly}>
                                    Hoàn thành đánh giá và yêu cầu sửa lại
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
                {isOpenDrawerComment &&
                    <CommentComponent topicId={entities?.documentId} handleDrawerComment={handleCloseDrawerComment} isOpenDrawerComment={isOpenDrawerComment} />
                }
                <ModalConfirmComponent
                    visible={isOpenModalConfirmComplete}
                    onCancel={() => {
                        setOpenModalConfirmComplete(false);
                    }}
                    onConfirm={handleConfirmComplete}
                    isLoading={updating}
                    title={`Bạn đã hoàn thành đánh giá?`}
                />
                <ModalRejectComponent
                    visible={isOpenModalRejectProcess}
                    onCancel={() => {
                        setOpenModalRejectProcess(false);
                    }}
                    onReject={handleCancelProcess}
                    title={`Yêu cầu sửa lại`}
                />
                {isOpenModalOtherComment && <OtherCommentEvaluationComponent
                    data={otherComment}
                    isLoading={isLoadingComment}
                    nameCriteria={plusPoints[currentIndexCriteria].name}
                    isOpen={isOpenModalOtherComment} onClose={handleCloseModalOtherComment} />}
                {isOpenModalOtherGeneralComment && <OtherGeneralCommentComponent
                    data={otherComment}
                    isLoading={isLoadingComment}
                    isOpen={isOpenModalOtherGeneralComment} onClose={handleCloseModalOtherGeneralComment} />}
                {isOpenModalOtherPoint && <OtherPointsEvaluationComponent
                    data={otherPoint}
                    isLoading={isLoadingPoint}
                    isOpen={isOpenModalOtherPoint} onClose={handleCloseModalOtherPoint} />}
            </Spin>
        </Modal>
    );
};

export default EvaConfirmCompleteEvaluation;
